// domain/attendance.ts — kategori kehadiran (murni). Port attKategori v0.18.
export type AttKat = 'ontime' | 'telat' | 'setengah' | 'alpha';

/** 'HH:MM' → menit sejak 00:00. */
export function toMinutes(hhmm: string): number {
  const [h, m] = (hhmm || '0:0').split(':').map((x) => +x || 0);
  return (h ?? 0) * 60 + (m ?? 0);
}

/** Kategori dari selisih menit clock-in vs jam masuk shift + toleransi/ambang. */
export function attKategori(masukMenit: number, shiftMasukMenit: number, toleransi: number, telatSetengah: number, telatAlpha: number): AttKat {
  const late = masukMenit - shiftMasukMenit;
  if (late <= toleransi) return 'ontime';
  if (late > telatAlpha) return 'alpha';       // strict > sesuai v0.18 (di menit pas batas = kategori lebih ringan)
  if (late > telatSetengah) return 'setengah';
  return 'telat';
}

export const KAT_LABEL: Record<AttKat, string> = { ontime: 'Tepat', telat: 'Telat', setengah: '½ Hari', alpha: 'Alpha' };

/** Kategori EFEKTIF setelah koreksi/approval (port attEffKat v0.18): override menang; req ditolak → alpha; else kategori asli.
 *  Dipakai rekap + filter + KPI kehadiran (bukan kategori mentah). */
export function attEffKat(rec: { kategori: string | null; kat_override?: string | null; req_status?: string | null }): AttKat | null {
  if (rec.kat_override) return rec.kat_override as AttKat;
  if (rec.req_status === 'rejected') return 'alpha';
  return (rec.kategori as AttKat) ?? null;
}

/** Jarak Haversine (meter) antara 2 koordinat. Port haversine v0.18 (geofence anti-fake-GPS). MURNI. */
export function haversine(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000; // radius bumi (m)
  const toRad = (d: number): number => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1), dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}
/** Apakah titik (lat2,lng2) berada dalam radius (m) dari pusat (lat1,lng1). */
export const withinRadius = (lat1: number, lng1: number, lat2: number, lng2: number, radiusM: number): boolean =>
  haversine(lat1, lng1, lat2, lng2) <= radiusM;

/** Apakah `iso` (YYYY-MM-DD) adalah hari kerja outlet. hariKerja = array 0=Min..6=Sab (JS getDay). */
export function isWorkday(hariKerja: number[], iso: string): boolean {
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d.getTime())) return false;
  return hariKerja.includes(d.getDay());
}

/** Daftar tanggal ISO 'YYYY-MM-DD' dalam periode. mode M=bulan m; Q=kuartal q; Y=seluruh tahun. Kalender lokal. */
export function tanggalPeriode(y: number, m: number, q: number, mode: 'M' | 'Q' | 'Y'): string[] {
  const bulan = mode === 'M' ? [m] : mode === 'Q' ? [(q - 1) * 3 + 1, (q - 1) * 3 + 2, (q - 1) * 3 + 3] : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const out: string[] = [];
  for (const mo of bulan) {
    if (mo < 1 || mo > 12) continue;
    const hariAkhir = new Date(y, mo, 0).getDate(); // hari terakhir bulan mo
    for (let d = 1; d <= hariAkhir; d++) out.push(`${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
  }
  return out;
}

/**
 * HARI KERJA EFEKTIF karyawan di periode = hari terjadwal kerja (hariKerja getDay) − hari libur −
 * hari cuti approved yang jatuh di hari kerja. Inilah denominator kehadiran yang benar: hari kerja
 * tanpa record absensi = ALPHA (tak masuk). Tanpa ini, yang bolos total malah dinilai hadir 100%.
 * `leaveRanges` = cuti APPROVED karyawan itu (rentang inklusif 'YYYY-MM-DD').
 */
export function hariKerjaEfektif(hariKerja: number[], holidays: string[], leaveRanges: { from: string; to: string }[], y: number, m: number, q: number, mode: 'M' | 'Q' | 'Y', asOf?: string): number {
  const hol = new Set(holidays);
  const cuti = new Set<string>();
  const DAY = 86400000;
  for (const r of leaveRanges) {
    const s = Date.parse(r.from + 'T00:00:00Z'), e = Date.parse(r.to + 'T00:00:00Z');
    if (Number.isNaN(s) || Number.isNaN(e) || e < s) continue;
    for (let t = s; t <= e; t += DAY) { const dt = new Date(t); cuti.add(`${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`); }
  }
  let n = 0;
  for (const iso of tanggalPeriode(y, m, q, mode)) {
    if (asOf && iso > asOf) continue;       // JANGAN vonis hari yang belum terjadi (paritas v0.18 attStat "≤ hari ini")
    if (hol.has(iso) || cuti.has(iso)) continue;
    if (isWorkday(hariKerja, iso)) n++;
  }
  return n;
}

/** Reminder masuk kerja: now dalam window [jamMasuk−reminderMenit, jamMasuk] & belum absen. Port v0.18 runReminders. MURNI. */
export function shouldRemindMasuk(nowMin: number, jamMasukMin: number, reminderMenit: number, sudahAbsen: boolean): boolean {
  if (sudahAbsen || reminderMenit <= 0) return false;
  return nowMin >= jamMasukMin - reminderMenit && nowMin <= jamMasukMin;
}

/** Menit keterlambatan (untuk Laporan Telat) = maks(0, masuk − jam masuk shift). Menit mentah (bukan kategori). */
export const menitTelat = (masukMin: number, jamMasukMin: number): number => Math.max(0, masukMin - jamMasukMin);

/** Device-binding (port deviceOK v0.18): cocokkan device tersimpan vs device saat ini (proxy token+fp, bukan IMEI).
 *  Belum terdaftar → {bound:false, ok:true} (akan didaftarkan absen pertama). Terdaftar → ok bila token & fp cocok. */
export interface DeviceRec { token: string; fp: string }
export function deviceMatch(stored: DeviceRec | null, curToken: string, curFp: string): { bound: boolean; ok: boolean } {
  if (!stored) return { bound: false, ok: true };
  return { bound: true, ok: stored.token === curToken && stored.fp === curFp };
}

/** Jumlah hari cuti dari rentang [from,to] (inklusif) yang jatuh di bulan/tahun terpilih (m=0 = seluruh tahun).
 *  TZ-safe: parse & bandingkan komponen UTC (hindari drift zona waktu / DST). from/to = 'YYYY-MM-DD'. */
export function cutiDaysInPeriod(ranges: { from: string; to: string }[], y: number, m: number): number {
  const DAY = 86400000;
  let days = 0;
  for (const r of ranges) {
    const start = Date.parse(r.from + 'T00:00:00Z'), end = Date.parse(r.to + 'T00:00:00Z');
    if (Number.isNaN(start) || Number.isNaN(end) || end < start) continue;
    for (let t = start; t <= end; t += DAY) {
      const dt = new Date(t);
      if (dt.getUTCFullYear() === y && (m === 0 || dt.getUTCMonth() + 1 === m)) days++;
    }
  }
  return days;
}

/** Satu sesi istirahat. Sesi terakhir tanpa `kembaliAt` berarti orangnya sedang istirahat. */
export interface SesiIstirahat {
  keluarAt: string; keluarLat?: number | null; keluarLng?: number | null;
  keluarInRadius?: boolean | null; keluarSelfie?: string | null;
  kembaliAt?: string | null; kembaliLat?: number | null; kembaliLng?: number | null;
  kembaliInRadius?: boolean | null; kembaliSelfie?: string | null;
  /** Jatah menit yang berlaku saat hari itu dimulai — dibekukan supaya mengubah kebijakan
   *  hari ini tidak menulis ulang penilaian bulan lalu (pola yang sama dengan `kategori`). */
  jatah?: number;
}

const menitAntara = (a: string, b: string): number => {
  const t1 = Date.parse(a), t2 = Date.parse(b);
  if (!Number.isFinite(t1) || !Number.isFinite(t2)) return 0;   // data cacat → 0, jangan jatuhkan rekap
  return Math.max(0, Math.round((t2 - t1) / 60000));            // jam mundur → 0, bukan menit negatif
};

/** Total menit istirahat yang SUDAH selesai. Sesi yang belum kembali belum dihitung. */
export function totalIstirahatMenit(sesi: SesiIstirahat[] | null | undefined): number {
  if (!Array.isArray(sesi)) return 0;
  return sesi.reduce((a, s) => a + (s?.kembaliAt ? menitAntara(s.keluarAt, s.kembaliAt) : 0), 0);
}

/** Sedang istirahat = ada sesi terbuka (keluar tapi belum kembali). */
export const sedangIstirahat = (sesi: SesiIstirahat[] | null | undefined): boolean =>
  Array.isArray(sesi) && sesi.some((s) => s?.keluarAt && !s.kembaliAt);

/** Kelebihan menit dari jatah. Jatah 0 = tidak dibatasi. */
export const lebihIstirahat = (totalMenit: number, jatahMenit: number): number =>
  jatahMenit > 0 ? Math.max(0, totalMenit - jatahMenit) : 0;

/** Jatah yang berlaku untuk satu hari: yang dibekukan di hari itu bila ada, else setelan sekarang. */
export const jatahBerlaku = (sesi: SesiIstirahat[] | null | undefined, jatahSekarang: number): number =>
  (Array.isArray(sesi) && typeof sesi[0]?.jatah === 'number') ? sesi[0].jatah : jatahSekarang;

interface RecIstirahat {
  kategori: string | null; kat_override?: string | null; req_status?: string | null;
  keluar_at?: string | null; istirahat?: SesiIstirahat[] | null;
}

/** Menit istirahat yang dipakai menilai. Sesi yang belum ditutup dihitung sampai jam pulang;
 *  bila jam pulang juga tak ada, hari itu ditandai `menggantung` — kalau tidak, cara termurah
 *  menghindari penilaian adalah dengan tidak menekan "Kembali Kerja" sama sekali. */
export function menitIstirahatEfektif(rec: RecIstirahat): { menit: number; menggantung: boolean } {
  const sesi = Array.isArray(rec.istirahat) ? rec.istirahat : [];
  let menit = 0, menggantung = false;
  for (const s of sesi) {
    if (!s?.keluarAt) continue;
    const tutup = s.kembaliAt ?? rec.keluar_at ?? null;
    if (tutup) menit += menitAntara(s.keluarAt, tutup);
    else menggantung = true;
  }
  return { menit, menggantung };
}

/** Hari dihitung "tepat" untuk KPI (aturan Apo 5 Agu 2026): kategori efektifnya ontime DAN
 *  istirahatnya tak lewat jatah. Hari yang sudah telat/½/alpha tak dihukum dua kali.
 *  KOREKSI ATASAN MENANG: hari yang sudah disetujui jadi ontime tetap dihitung tepat — kolom
 *  istirahat terkunci di database, jadi tanpa jalan ini satu salah-pencet tak bisa diperbaiki
 *  siapa pun. Override hanya bisa dibuat approver (dijaga trigger koreksi kehadiran). */
export function hariTepatUntukKpi(rec: RecIstirahat, jatahSekarang: number): boolean {
  if (attEffKat(rec) !== 'ontime') return false;
  if (rec.kat_override) return true;
  const jatah = jatahBerlaku(rec.istirahat, jatahSekarang);
  if (jatah <= 0) return true;                                  // tak dibatasi
  const { menit, menggantung } = menitIstirahatEfektif(rec);
  return !menggantung && lebihIstirahat(menit, jatah) === 0;
}

// ── Rekap kehadiran per orang (murni) ────────────────────────────────────────────────────
// Sebelumnya agregasi ini hidup di dalam `renderRekapAbsensi` sehingga tak bisa dipakai layar
// lain. Penggajian butuh angka yang SAMA PERSIS dengan yang dibaca atasan di Rekap Absensi —
// kalau dihitung ulang di tempat lain, cepat atau lambat dua layar akan menyebut jumlah alpha
// yang berbeda untuk orang yang sama, dan yang satu dipakai memotong gaji.

export interface RekapKehadiran {
  nama: string; role: string;
  ontime: number; telat: number; setengah: number; alpha: number;
  cepat: number; hadir: number; cuti: number; korr: number;
  istMnt: number; istLebih: number;
}

interface RecRekap extends RecIstirahat {
  emp_id: number; nama: string; role: string; tgl: string;
  keluar_at?: string | null; shiftKeluar?: string | null;
}

/**
 * Rekap per karyawan untuk catatan yang sudah disaring ke satu periode.
 * `pulangCepat` disuntikkan pemanggil karena butuh jam shift (bukan urusan domain).
 */
export function rekapKehadiran<T extends RecRekap>(
  recs: T[],
  jatahIstirahatSekarang: number,
  pulangCepat: (r: T) => boolean,
): Map<number, RekapKehadiran> {
  const out = new Map<number, RekapKehadiran>();
  for (const r of recs) {
    const s = out.get(r.emp_id) ?? { nama: r.nama, role: r.role, ontime: 0, telat: 0, setengah: 0, alpha: 0, cepat: 0, hadir: 0, cuti: 0, korr: 0, istMnt: 0, istLebih: 0 };
    const k = attEffKat(r);                       // kategori EFEKTIF (hormati koreksi/approval)
    if (k === 'ontime' || k === 'telat' || k === 'setengah' || k === 'alpha') s[k]++;
    if (k && k !== 'alpha') s.hadir++;
    if (pulangCepat(r)) s.cepat++;
    const im = menitIstirahatEfektif(r).menit;
    s.istMnt += im;
    s.istLebih += lebihIstirahat(im, jatahBerlaku(r.istirahat, jatahIstirahatSekarang));
    if (r.req_status || r.kat_override) s.korr++;
    out.set(r.emp_id, s);
  }
  return out;
}
